import { apiClient } from './client';
import type {
  ReservationStoreInfoResponseDto,
  OnlineReservationRequestResponseDto,
  OnlineReservationDto,
  Reservations,
} from './generated';

export interface TimeslotParams {
  date: string; // YYYY-MM-DD format
  adults: number;
  children?: number;
}

export interface ApiError {
  code?: string;
  message: string;
  salaError?: string;
}

/**
 * Reservation API functions
 */
export const reservationApi = {
  /**
   * Get store reservation information and settings
   */
  getStoreInfo: async (storeDomain: string): Promise<ReservationStoreInfoResponseDto> => {
    const response = await apiClient.get<ReservationStoreInfoResponseDto>(
      `/v1/public/stores/${storeDomain}/reservations/info`
    );
    return response.data;
  },

  /**
   * Get available time slots for a specific date and number of people
   */
  getTimeslots: async (
    storeDomain: string,
    params: TimeslotParams
  ): Promise<OnlineReservationRequestResponseDto[]> => {
    const response = await apiClient.get<OnlineReservationRequestResponseDto[]>(
      `/v1/public/stores/${storeDomain}/reservations/time-slots`,
      { params }
    );
    return response.data;
  },

  /**
   * Create a new reservation
   */
  createReservation: async (
    storeDomain: string,
    data: OnlineReservationDto
  ): Promise<Reservations> => {
    const response = await apiClient.post<Reservations>(
      `/v1/public/stores/${storeDomain}/reservations`,
      data
    );
    return response.data;
  },

  /**
   * Get a specific reservation by UUID
   */
  getReservation: async (
    storeDomain: string,
    reservationUuid: string
  ): Promise<Reservations> => {
    const response = await apiClient.get<Reservations>(
      `/v1/public/stores/${storeDomain}/reservations/${reservationUuid}`
    );
    return response.data;
  },

  /**
   * Confirm a pending reservation
   */
  confirmReservation: async (
    storeDomain: string,
    reservationUuid: string
  ): Promise<void> => {
    await apiClient.post(
      `/v1/public/stores/${storeDomain}/reservations/confirm/${reservationUuid}`
    );
  },

  /**
   * Cancel a reservation
   */
  cancelReservation: async (
    storeDomain: string,
    reservationUuid: string
  ): Promise<void> => {
    await apiClient.post(
      `/v1/public/stores/${storeDomain}/reservations/cancel/${reservationUuid}`
    );
  },
};
